import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { getLocations, setSelected as setLocation } from '../redux/location'
import { Table, Drawer, Space, Button, List, Modal, InputNumber, Popconfirm, Select, Spin } from 'antd';
import EditLocation from '../components/EditLocation';
import CreateLocation from '../components/CreateLocation';
import { PlusCircleOutlined, SettingOutlined } from '@ant-design/icons';
import { useBathAddonModal } from '../hooks/useBathAddonModal';

const Locations = ({ selected, locations, locationIds, setLocation, getLocations }) => {
	const [isCreateOpen, setCreateOpen] = useState(false);
	const [isEditOpen, setEditOpen] = useState(false);
	const [tableData, setTableData] = useState(null);
	const [tableColumns, setTableColumns] = useState(null);
	const {
		state: bathState,
		openModal: openBathModal,
		closeModal: closeBathModal,
		save: handleBathSave,
		remove: handleBathDelete,
		setNightThreshold: setBathNightThreshold,
		setBathServiceId,
	} = useBathAddonModal(getLocations);

	useEffect(() => {
		getLocations();
	}, [getLocations]);

	useEffect(() => {
		if (Array.isArray(locationIds)) {
			const _locations = locationIds.map(locId => {
				return { key: locId, ...locations[locId] };
			});
			setTableData(_locations);
		}
		const _columns = [
			{
				title: 'ID',
				dataIndex: 'id',
			},
			{
				title: 'Name',
				dataIndex: 'name',
				render: (text, row) => {
					return (
						<>
							<p>{text}</p>
							<small>{row.address}</small>
						</>
					)
				}
			},
			{
				title: 'Vet Services',
				dataIndex: 'hasVet',
				render: (val) => {
					return (
						<p>{val === 1 ? 'Yes' : 'No'}</p>
					)
				}
			},
			{
				title: 'Has Dog Training',
				dataIndex: 'hasDogTraining',
				render: (val) => {
					return (
						<p>{val ? 'Yes' : 'No'}</p>
					)
				}
			},
			{
				title: 'Alternates',
				dataIndex: 'alternates',
				render: (val, row) => {
					return (
						<List
							size="small"
							dataSource={row.alternates}
							renderItem={(alt) => < List.Item > {locations[alt.locId].name}</ List.Item >} />
					)
				}
			},
			{
				title: 'Non-Compat',
				dataIndex: 'noncompats',
				render: (val, row) => {
					return (
						<List
							size="small"
							dataSource={row.noncompats}
							renderItem={(item) => <List.Item>{item.name} (ID: {item.nonCompatId})</List.Item>} />
					)
				}
			},
			{
				title: 'Bath Night Threshold',
				dataIndex: 'bathAddonLocation',
				render: (val) => val ? val.nightThreshold : '-',
			},
			{
				title: 'Bath Service ID',
				dataIndex: 'bathAddonLocation',
				render: (val) => val ? val.bathServiceId : '-',
			},
			{
				title: 'Bath Add-On',
				dataIndex: 'bathAddonLocation',
				render: (val) => val ? 'Enabled' : 'Disabled',
			},
			{
				title: 'Actions',
				render: (text, row) => (
					<Button
						icon={<SettingOutlined />}
						onClick={(e) => {
							e.stopPropagation();
							openBathModal(row);
						}}
					>
						Configure
					</Button>
				),
			},
		];
		setTableColumns(_columns);

	}, [locations, locationIds]);

	const onDrawerClose = () => {
		setLocation(null);
		setEditOpen(false);
		setCreateOpen(false);
	};

	const onRowClick = (location) => {
		setLocation(location);
		setCreateOpen(false);
		setEditOpen(true);
	}
	return (
		<div>
			<Space style={{ float: 'right', marginBottom: 16 }}>
				<Button type="primary" onClick={() => setCreateOpen(true)} style={{ background: '#008E87' }}>
					<PlusCircleOutlined /> New Location
				</Button>
			</Space>
			{tableData && <Table
				dataSource={tableData}
				columns={tableColumns}
				pagination={false}
				onRow={(record) => {
					return {
						onClick: () => { onRowClick(record) },
					};
				}} />}
			{selected && <Drawer
				title={`Edit ${selected?.name} [${selected.id}]`}
				placement="right"
				size={'large'}
				width={900}
				onClose={onDrawerClose}
				open={isEditOpen}
				extra={
					<Space>
						<Button onClick={onDrawerClose}>Cancel</Button>
						<Button type="primary" form="edit-location" htmlType='submit'>
							Update
						</Button>
					</Space>
				}>
				{selected && <EditLocation onClose={onDrawerClose} />}
			</Drawer>}
			<Drawer
				title="New Location"
				placement="right"
				size={'large'}
				width={900}
				onClose={onDrawerClose}
				open={isCreateOpen}
				extra={
					<Space>
						<Button onClick={onDrawerClose}>Cancel</Button>
						<Button type="primary" form="create-location" htmlType='submit'>
							Create
						</Button>
					</Space>
				}>
				{isCreateOpen && <CreateLocation onClose={onDrawerClose} />}
			</Drawer>
			<Modal
				title={`Bath Add-On - ${bathState.location?.name ?? ''}`}
				open={bathState.visible}
				onCancel={closeBathModal}
				footer={
					<Space>
						<Button onClick={closeBathModal}>Cancel</Button>
						{bathState.config && (
							<Popconfirm
								title="Please Confirm"
								description="Are you sure you want to delete this bath add-on configuration?"
								onConfirm={handleBathDelete}
								okText="Yes"
								cancelText="No"
							>
								<Button danger loading={bathState.saving}>
									Delete
								</Button>
							</Popconfirm>
						)}
						<Button type="primary" loading={bathState.saving} disabled={bathState.loading || (!bathState.config && bathState.services.length === 0)} onClick={handleBathSave} style={{ background: '#008E87' }}>
							{bathState.config ? 'Save' : 'Create'}
						</Button>
					</Space>
				}
			>
				{bathState.loading ? (
					<div style={{ textAlign: 'center', padding: 48 }}>
						<Spin size="large" />
						<p style={{ marginTop: 16, color: '#666' }}>Loading bath services...</p>
					</div>
				) : bathState.services.length === 0 && !bathState.config ? (
					<p style={{ textAlign: 'center', padding: 24, color: '#999' }}>
						No bath services available for the boarding reservation type at this location.
					</p>
				) : (
					<div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '24px 0' }}>
						<div>
							<label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Night Threshold</label>
							<InputNumber
								min={1}
								value={bathState.nightThreshold}
								onChange={(val) => setBathNightThreshold(val)}
								style={{ width: '100%' }}
								placeholder="Minimum nights before bath add-on applies"
							/>
						</div>
						<div>
							<label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Bath Service</label>
							<Select
								showSearch
								placeholder="Select a bath service"
								value={bathState.bathServiceId}
								onChange={(val) => setBathServiceId(val)}
								style={{ width: '100%' }}
								notFoundContent="No bath services found"
								optionFilterProp="label"
								filterSort={(optionA, optionB) =>
									(optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
								}
								options={bathState.services.map((svc) => ({
									label: `${svc.name} (ID: ${svc.id})`,
									value: svc.id,
								}))}
							/>
						</div>
					</div>
				)}
			</Modal>
		</div>

	);
};

const mapStateToProps = (state) => {
	return {
		...state.locations
	};
};

const mapDispatchToProps = { getLocations, setLocation };

export default connect(mapStateToProps, mapDispatchToProps)(Locations);
